import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { MessageSquare, Save, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { WhatsAppConfig as WhatsAppConfigType } from '../types/whatsapp-config';

export function WhatsAppConfig() {
  const [config, setConfig] = useState<WhatsAppConfigType>({
    enabled: false,
    phoneNumber: '',
    countryCode: '+27',
    messageTemplates: {
      propertyInquiry: '',
      generalInquiry: '',
      viewingRequest: '',
    },
    autoReply: {
      enabled: false,
      message: '',
    },
    businessHours: {
      enabled: false,
      monday: { open: '08:00', close: '17:00', active: true },
      tuesday: { open: '08:00', close: '17:00', active: true },
      wednesday: { open: '08:00', close: '17:00', active: true },
      thursday: { open: '08:00', close: '17:00', active: true },
      friday: { open: '08:00', close: '17:00', active: true },
      saturday: { open: '09:00', close: '13:00', active: false },
      sunday: { open: '09:00', close: '13:00', active: false },
    },
  });

  const handleSave = () => {
    toast.success('WhatsApp configuration saved successfully');
  };

  const updateConfig = (updates: Partial<WhatsAppConfigType>) => {
    setConfig({ ...config, ...updates });
  };

  const updateMessageTemplate = (key: keyof typeof config.messageTemplates, value: string) => {
    setConfig({
      ...config,
      messageTemplates: {
        ...config.messageTemplates,
        [key]: value,
      },
    });
  };

  const updateBusinessHours = (day: keyof typeof config.businessHours, updates: any) => {
    if (day === 'enabled') {
      setConfig({
        ...config,
        businessHours: {
          ...config.businessHours,
          enabled: updates,
        },
      });
    } else {
      setConfig({
        ...config,
        businessHours: {
          ...config.businessHours,
          [day]: {
            ...config.businessHours[day],
            ...updates,
          },
        },
      });
    }
  };

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ] as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3>WhatsApp Configuration</h3>
          <p className="text-muted-foreground">
            Configure WhatsApp integration for property inquiries
          </p>
        </div>
        <Button onClick={handleSave} data-testid="whatsapp-config-save-button">
          <Save className="w-4 h-4 mr-2" />
          Save Configuration
        </Button>
      </div>

      {/* Basic Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Settings</CardTitle>
          <CardDescription>
            Configure your WhatsApp business account details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable WhatsApp Integration</Label>
              <p className="text-sm text-muted-foreground">
                Allow users to contact you via WhatsApp
              </p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => updateConfig({ enabled: checked })}
            />
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="countryCode">Country Code</Label>
              <Input
                id="countryCode"
                value={config.countryCode}
                onChange={(e) => updateConfig({ countryCode: e.target.value })}
                placeholder="+27"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                value={config.phoneNumber}
                onChange={(e) => updateConfig({ phoneNumber: e.target.value })}
                placeholder="821234567"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              WhatsApp Number: {config.countryCode} {config.phoneNumber}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Message Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Message Templates</CardTitle>
          <CardDescription>
            Customize automatic message templates. Use {'{'}propertyTitle{'}'}, {'{'}propertyAddress{'}'} as placeholders.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="propertyInquiry">Property Inquiry Template</Label>
            <Textarea
              id="propertyInquiry"
              rows={3}
              value={config.messageTemplates.propertyInquiry}
              onChange={(e) => updateMessageTemplate('propertyInquiry', e.target.value)}
              placeholder="Template for property inquiries..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="generalInquiry">General Inquiry Template</Label>
            <Textarea
              id="generalInquiry"
              rows={3}
              value={config.messageTemplates.generalInquiry}
              onChange={(e) => updateMessageTemplate('generalInquiry', e.target.value)}
              placeholder="Template for general inquiries..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="viewingRequest">Viewing Request Template</Label>
            <Textarea
              id="viewingRequest"
              rows={3}
              value={config.messageTemplates.viewingRequest}
              onChange={(e) => updateMessageTemplate('viewingRequest', e.target.value)}
              placeholder="Template for viewing requests..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Auto Reply */}
      <Card>
        <CardHeader>
          <CardTitle>Auto Reply</CardTitle>
          <CardDescription>
            Automatically respond to incoming messages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Auto Reply</Label>
              <p className="text-sm text-muted-foreground">
                Send automatic replies outside business hours
              </p>
            </div>
            <Switch
              checked={config.autoReply.enabled}
              onCheckedChange={(checked) =>
                setConfig({
                  ...config,
                  autoReply: { ...config.autoReply, enabled: checked },
                })
              }
            />
          </div>

          {config.autoReply.enabled && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="autoReplyMessage">Auto Reply Message</Label>
                <Textarea
                  id="autoReplyMessage"
                  rows={3}
                  value={config.autoReply.message}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      autoReply: { ...config.autoReply, message: e.target.value },
                    })
                  }
                  placeholder="Thank you for your message..."
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card>
        <CardHeader>
          <CardTitle>Business Hours</CardTitle>
          <CardDescription>
            Set your availability for WhatsApp inquiries
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Business Hours</Label>
              <p className="text-sm text-muted-foreground">
                Define when you're available to respond
              </p>
            </div>
            <Switch
              checked={config.businessHours.enabled}
              onCheckedChange={(checked) => updateBusinessHours('enabled', checked)}
            />
          </div>

          {config.businessHours.enabled && (
            <>
              <Separator />
              <div className="space-y-3">
                {days.map(({ key, label }) => {
                  const hours = config.businessHours[key];
                  return (
                    <div key={key} className="flex items-center gap-4">
                      <div className="w-28">
                        <Switch
                          checked={hours.active}
                          onCheckedChange={(checked) =>
                            updateBusinessHours(key, { active: checked })
                          }
                        />
                        <Label className="ml-2">{label}</Label>
                      </div>

                      {hours.active && (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={hours.open}
                            onChange={(e) =>
                              updateBusinessHours(key, { open: e.target.value })
                            }
                            className="w-32"
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={hours.close}
                            onChange={(e) =>
                              updateBusinessHours(key, { close: e.target.value })
                            }
                            className="w-32"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
